// Path: src/lib/apis_vibe.ts
import { api } from './api';

// ==========================================
// 1. 类型定义 (Types)
// ==========================================

// --- 基础实体 ---
export interface UserWallet {
  id: number;
  user_id: number;
  balance: number;
  total_used: number;
  used_money?: number;
  updated_at: string;
}

export interface StudentWallet extends UserWallet {
  username: string;
  role: string;
  memo?: string;
  school_id?: number;
  usage_today: number;
  usage_yesterday: number;
  usage_day_before_yesterday: number;
  usage_today_money?: number;
  usage_yesterday_money?: number;
  usage_day_before_yesterday_money?: number;
}

export interface TokenUsageLog {
  id: number;
  user_id: number;
  related_type: string;
  related_id?: number;
  amount: number;
  input_tokens?: number;
  output_tokens?: number;
  input_money?: number;
  output_money?: number;
  total_money?: number;
  remark?: string;
  created_at: string;
  user?: {
    username: string;
    role: string;
    memo?: string;
  };
}

export interface ProjectFile {
  id: number;
  project_id: number;
  name: string;
  file_type: string;
  oss_url?: string | null;
  latest_version_id?: number;
  latest_version_img_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: number;
  user_id: number;
  name: string;
  description?: string | null;
  created_at: string;
  updated_at: string;
  files?: ProjectFile[];
}

export interface FileVersion {
  id: number;
  file_id: number;
  version_number: number;
  html_code?: string | null;
  commit_message?: string | null;
  created_by: 'user' | 'ai';
  created_at: string;
}

// 🔥 ChatMessage 支持多模态
export interface ChatMessage {
  id: number | string; // 支持本地 optimistic ID
  version_id?: number;
  role: 'user' | 'assistant';
  content: string;
  
  // 兼容旧字段 (用户上传的图片URL列表)
  image_urls?: string[] | string | null;
  
  // 新字段 (结构化文件列表，含 AI 生成图)
  files?: Array<{ 
    oss_url: string; 
    file_type: 'image_input' | 'image_output' | 'document';
  }>;

  input_tokens: number;
  output_tokens: number;
  input_money?: number;
  output_money?: number;
  total_money?: number;  // 累计总金额
  created_at: string;
  // 流式消息相关：
  reasoningText?: string;   // 当前可删除提示文本（reasoning/progress）
  isThinking?: boolean;     // 是否处于思考阶段（有提示且无正文）
  partial?: boolean;        // 内容是否可能不完整（meta.partial）
  partialReason?: string;   // 不完整原因
}

// --- 响应结果类型 ---

export interface PublishResult {
  version_id: number;
  file_id: number;
  file_name: string;
  oss_url: string;
  img_url?: string | null;
  audio_url?: string | null;
  audio_msg?: string | null;
  video_url?: string | null;
  video_1st_url?: string | null;
  qr_url?: string | null;
  count?: number;
  first_message_id?: number;
  first_message_content?: string;
  first_message_role?: string;
  first_message_created_at?: string;
}

export interface UploadImageResult {
  url: string;
  // 有些接口可能返回 oss_url，这里做兼容
  oss_url?: string;
}

export interface BatchRechargeResult {
  school_id: number;
  success_count: number;
  total_amount_added: number;
  message: string;
}

// --- 请求载荷类型 (Payloads) ---

export interface ProjectCreatePayload {
  name: string;
  description?: string;
}

// 🔥 新增：项目更新载荷
export interface ProjectUpdatePayload {
  name?: string;
  description?: string;
}

export interface FileCreatePayload {
  name: string;
  file_type?: string;
}

// 🔥 新增：文件更新载荷
export interface FileUpdatePayload {
  name?: string;
  oss_url?: string;
}

export interface VersionCreatePayload {
  html_code: string;
  commit_message?: string;
  created_by?: 'user' | 'ai';
}

export interface TokenRechargePayload {
  user_id: number;
  amount: number; // 正数充值，负数扣减
  remark?: string;
}

export interface BatchRechargePayload {
  school_id: number;
  amount: number;
  remark?: string;
}

// ==========================================
// 💰 金额格式化工具
// ==========================================

/** 格式化金额为 ¥xxxx.xxxx（4 位小数，无符号） */
export const formatMoney = (money?: number | null): string => {
  const v = Number(money);
  return `¥${(isNaN(v) ? 0 : Math.abs(v)).toFixed(4)}`;
};

/** 格式化带符号金额（充值 +、消耗 -），符号由 amount 符号决定 */
export const formatSignedMoney = (money: number | undefined | null, isRecharge: boolean): string => {
  const v = Math.abs(Number(money) || 0);
  return isRecharge ? `+¥${v.toFixed(4)}` : `-¥${v.toFixed(4)}`;
};

/** 格式化净额（可正可负） */
export const formatNetMoney = (money: number): string => {
  const v = Number(money) || 0;
  if (v > 0) return `+¥${v.toFixed(4)}`;
  if (v < 0) return `-¥${Math.abs(v).toFixed(4)}`;
  return `¥0.0000`;
};

// ==========================================
// 2. API 函数封装
// ==========================================

const BASE_URL = '/vibe-coding';

// --- C端：学员钱包 ---

export const getWallet = async () => {
  return api.get<UserWallet>(`${BASE_URL}/wallet/`);
};

// --- 项目管理 ---

export const getProjects = async (skip = 0, limit = 100) => {
  return api.get<Project[]>(`${BASE_URL}/projects/`, {
    params: { skip, limit }
  });
};

export const createProject = async (data: ProjectCreatePayload) => {
  return api.post<Project>(`${BASE_URL}/projects/`, data);
};

// 🔥 新增：更新项目（重命名）
export const updateProject = async (projectId: number, data: ProjectUpdatePayload) => {
  return api.put<Project>(`${BASE_URL}/projects/${projectId}/`, data);
};

export const getProjectDetail = async (projectId: number) => {
  return api.get<Project>(`${BASE_URL}/projects/${projectId}/`);
};

export const softDeleteProject = async (projectId: number) => {
  return api.delete<{ message: string; project_id: number }>(`${BASE_URL}/projects/${projectId}/soft`);
};

// --- 文件管理 ---

export const createFile = async (projectId: number, data: FileCreatePayload) => {
  return api.post<ProjectFile>(`${BASE_URL}/projects/${projectId}/files/`, data);
};

// 🔥 新增：更新文件（重命名）
export const updateFile = async (fileId: number, data: FileUpdatePayload) => {
  return api.put<ProjectFile>(`${BASE_URL}/files/${fileId}/`, data);
};

export const softDeleteFile = async (fileId: number) => {
  return api.delete<{ message: string; file_id: number }>(`${BASE_URL}/files/${fileId}/soft`);
};

export const updateFileOssUrl = async (fileId: number, ossUrl: string) => {
  return api.put<ProjectFile>(`${BASE_URL}/files/${fileId}/oss/`, null, {
    params: { oss_url: ossUrl }
  });
};

/**
 * 仅保存更新当前版本的内容，不要创建新版本
 */
export const saveFileVersion = async (fileId: number, versionId: number, data: VersionCreatePayload) => {
  return api.post<FileVersion>(`${BASE_URL}/files/${fileId}/versions/${versionId}/save/`, data);
};

// --- 版本管理与发布 ---

export const getFileVersions = async (fileId: number) => {
  return api.get<FileVersion[]>(`${BASE_URL}/files/${fileId}/versions/`);
};

export const getVersionDetail = async (versionId: number) => {
  return api.get<FileVersion>(`${BASE_URL}/versions/${versionId}/`);
};

export const softDeleteVersion = async (versionId: number) => {
  return api.delete<{ message: string; version_id: number }>(`${BASE_URL}/versions/${versionId}/soft`);
};

export const createVersion = async (fileId: number, data: VersionCreatePayload) => {
  return api.post<FileVersion>(`${BASE_URL}/files/${fileId}/versions/`, data);
};

/**
 * 发布/全屏预览 (将 HTML 推送到 OSS)
 * @param versionId 版本ID
 * @param name 可选，作品名称，如果提供则更新当前version所在file的name字段
 */
export const publishVersion = async (versionId: number, name?: string) => {
  return api.post<PublishResult>(`${BASE_URL}/versions/${versionId}/publish/`, name ? { name } : {});
};

// --- 聊天与多模态 ---

export const getVersionMessages = async (versionId: number, limit = 100) => {
  return api.get<ChatMessage[]>(`${BASE_URL}/versions/${versionId}/messages/`, { params: { limit } });
};

export const clearVersionMessages = async (versionId: number) => {
  return api.delete(`${BASE_URL}/versions/${versionId}/messages/`);
};

export const deleteMessage = async (messageId: number) => {
  return api.delete(`${BASE_URL}/messages/${messageId}/`);
};

/**
 * 仅删除单条用户消息
 * 不会删除对应的AI回复消息
 */
export const deleteUserMessage = async (messageId: number) => {
  return api.delete(`${BASE_URL}/messages/${messageId}/user/`);
};

export const uploadChatImage = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  
  // 🔥 显式添加 headers: { 'Content-Type': 'multipart/form-data' }
  // 虽然 Axios 通常会自动识别，但在某些拦截器配置下需要强制覆盖
  return api.post<UploadImageResult>(`${BASE_URL}/chat/images/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// --- B端：教师/管理员 Token 管理 ---

/**
 * 获取学生钱包列表 (支持按学校、角色、备注筛选)
 */
export const getStudentWallets = async (params: { school_id?: number; skip?: number; limit?: number; role?: string; memo?: string }) => {
  return api.get<StudentWallet[]>(`${BASE_URL}/admin/wallets/`, { params });
};

/**
 * 给单个学生充值/扣款
 */
export const rechargeStudentWallet = async (data: TokenRechargePayload) => {
  return api.post<UserWallet>(`${BASE_URL}/admin/wallets/recharge/`, data);
};

/**
 * 查看单个学生的 Token 消耗/充值流水
 */
export const getStudentTokenLogs = async (userId: number, skip = 0, limit = 50) => {
  return api.get<TokenUsageLog[]>(`${BASE_URL}/admin/wallets/${userId}/logs/`, {
    params: { skip, limit }
  });
};

/**
 * 获取某机构所有学生的 Token 报表 (支持按角色、备注筛选)
 */
export const getSchoolStudentWallets = async (schoolId: number, params?: { skip?: number; limit?: number; role?: string; memo?: string }) => {
  return api.get<StudentWallet[]>(`${BASE_URL}/admin/schools/${schoolId}/wallets/`, {
    params: { skip: params?.skip ?? 0, limit: params?.limit ?? 100, role: params?.role, memo: params?.memo }
  });
};

/**
 * 获取某机构下所有 Token 流水
 */
export const getSchoolTokenLogs = async (schoolId: number, skip = 0, limit = 1000) => {
  return api.get<TokenUsageLog[]>(`${BASE_URL}/admin/schools/${schoolId}/logs/`, {
    params: { skip, limit }
  });
};

/**
 * 给某机构所有学生批量充值
 */
export const batchRechargeSchool = async (data: BatchRechargePayload) => {
  return api.post<BatchRechargeResult>(`${BASE_URL}/admin/schools/batch-recharge/`, data);
};

// --- WebSocket 辅助 ---

export interface ImageUrlsResponse {
  version_id: number;
  image_urls: string[];
  count: number;
}

export interface DeleteImageResponse {
  success: boolean;
  message: string;
  oss_deleted: boolean;
  db_updated_count: number;
}

export interface GenCoverPromptResult {
  status: string;
  prompt: string;
  from_cache: boolean;
  input_tokens?: number;
  output_tokens?: number;
  input_money?: number;
  output_money?: number;
}

export interface GenCoverImgResult {
  image_url: string;
}

export interface UploadAudioResult {
  audio_url: string;
}

export interface UploadVideoResult {
  video_url: string;
}

export interface VersionMediaResult {
  version_id: number;
  file_id: number;
  file_name: string;
  img_url: string | null;
  audio_url: string | null;
  audio_msg: string | null;
  video_url: string | null;
  video_1st_url: string | null;
  qr_url: string | null;
}

export const getVersionImageUrls = async (versionId: number) => {
  return api.get<ImageUrlsResponse>(`${BASE_URL}/versions/${versionId}/image-urls/`);
};

export const deleteVersionImageUrl = async (versionId: number, imageUrl: string) => {
  return api.delete<DeleteImageResponse>(`${BASE_URL}/versions/${versionId}/image-urls/`, {
    data: { image_url: imageUrl }
  });
};

export const getChatWebSocketUrl = (versionId: number, token: string) => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  let host = window.location.host;
  
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';
    
    if (apiBaseUrl) {
      if (apiBaseUrl.startsWith('/')) {
        // 相对路径，使用当前 host（通过 Vite 代理）
        // host 保持不变
      } else {
        try {
          const url = new URL(apiBaseUrl);
          host = `${url.hostname}:${url.port || (url.protocol === 'https:' ? '443' : '80')}`;
        } catch (e) {
          console.warn("Invalid VITE_API_BASE_URL", e);
        }
      }
    }
  }
  
  return `${protocol}//${host}/api/v1/vibe-coding/ws/chat/${versionId}?token=${token}`;
};

export const genCoverPrompt = async (versionId: number) => {
  return api.post<GenCoverPromptResult>(`/vibe-publish/versions/${versionId}/gen-cover-prompt/`);
};

export const genCoverImg = async (versionId: number, prompt: string) => {
  return api.post<GenCoverImgResult>(`/vibe-publish/versions/${versionId}/gen-cover-img/`, { prompt });
};

export const uploadAudio = async (versionId: number, file: File, audioMsg?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (audioMsg) {
    formData.append('audio_msg', audioMsg);
  }
  return api.post<UploadAudioResult>(`/vibe-publish/versions/${versionId}/upload-audio/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const saveAudioMsg = async (versionId: number, audioMsg: string) => {
  return api.post<{ success: boolean }>(`/vibe-publish/versions/${versionId}/audio-msg/`, { audio_msg: audioMsg });
};

export const uploadVideo = async (versionId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<UploadVideoResult>(`/vibe-publish/versions/${versionId}/upload-video/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getVersionMedia = async (versionId: number) => {
  return api.get<VersionMediaResult>(`/vibe-publish/versions/${versionId}/media/`);
};

export const getVersionPublishInfo = async (versionId: number) => {
  return api.get<PublishResult>(`/vibe-coding/versions/${versionId}/publish-info/`);
};

// ==========================================
// 📊 Vibe Audit 评分与思维链
// ==========================================

export interface AuditDimension {
  name: string;
  score: number;
  tier: string;        // "未达标" | "合格" | "优秀"
  evidence: string;
}

export interface AuditSuggestion {
  title: string;
  highlight: string;
  probes: string[];    // 3-5 个递进反问句
}

export interface AuditEvaluation {
  dimensions: AuditDimension[];     // 10 个维度
  total_score: number;                // 0-100
  total_tier: string;                // "未成立" | "合格" | "扎实" | "优秀"
  summary: string;
  suggestions: AuditSuggestion[];    // 3 条苏格拉底式建议
}

export interface AuditResponse {
  version_id: number;
  lesson_id: number;
  status: 'success' | 'cached';
  evaluation: AuditEvaluation;
  raw_response?: string;
  audited_at: string | null;
  input_tokens: number;
  output_tokens: number;
  input_money: number;
  output_money: number;
}

export interface MindmapResponse {
  version_id: number;
  lesson_id: number | null;
  mindmap_lesson_id: number | null;
  status: 'success' | 'cached';
  message_count: number;
  svg_code: string;
  mindmap_at: string | null;
  input_tokens: number;
  output_tokens: number;
  input_money: number;
  output_money: number;
}

const AUDIT_BASE_URL = '/vibe-audit';

// AI 10 维度评分（默认 gemini_high 避免 gemini 超时）
export const evaluateVersion = async (
  lessonId: number,
  versionId: number,
  data?: { model?: string; force_refresh?: boolean }
) => {
  return api.post<AuditResponse>(
    `${AUDIT_BASE_URL}/lessons/${lessonId}/versions/${versionId}/evaluate/`,
    data || { model: 'gemini_high' }
  );
};

// 思维决策链 SVG（默认 gemini_high）
export const getVersionMindmap = async (
  versionId: number,
  data?: { model?: string; force_refresh?: boolean; lesson_id?: number }
) => {
  return api.post<MindmapResponse>(
    `${AUDIT_BASE_URL}/versions/${versionId}/mindmap/`,
    data || { model: 'gemini_high' }
  );
};
